/**
 * Cart Pack Actions — manage a configured pack as one unit in the cart.
 *
 * Pack line items all share a `_glend_pack` instance id property. This script
 * adds two pack-level actions, working on both the cart page and the cart drawer:
 *
 *   [data-pack-delete] — inline-confirm, then remove every line item of the pack
 *                        instance in a single /cart/update.js call.
 *   [data-pack-edit]   — collect the pack's tier + chosen products from /cart.js,
 *                        stash them in sessionStorage, and navigate to the pack
 *                        page so the configurator can restore them.
 *
 * Delete reloads the page afterwards (consistent with cart-subscription.js's
 * pack operations). Edit navigates away. So no cart section re-render is needed.
 */
(function () {
  'use strict';

  // This snippet is included from both cart-drawer.liquid (in the layout, so it's
  // on every page) and main-cart-items.liquid (the cart page section). Without
  // this guard, the cart page binds two document-level click listeners — one
  // "Sí, eliminar" click fires two parallel POST /cart/update.js calls, and the
  // losing concurrent write shows the "no se pudo eliminar" toast just before
  // the winning one reloads the page.
  if (window.__glendCartPackActionsInit) return;
  window.__glendCartPackActionsInit = true;

  var PACK_PROPERTY = '_glend_pack';
  var LABEL_PROPERTY = '_glend_pack_label';
  var SLOT_PROPERTY = '_glend_pack_slot';
  var EDIT_KEY = 'glend_pack_edit';
  var DEFAULT_PACK_URL = '/products/pack-algrano-bakes';

  document.addEventListener('click', function (e) {
    var confirmBtn = e.target.closest('[data-pack-delete-confirm]');
    if (confirmBtn) { onDeleteConfirm(confirmBtn); return; }

    var cancelBtn = e.target.closest('[data-pack-delete-cancel]');
    if (cancelBtn) { onDeleteCancel(cancelBtn); return; }

    var deleteBtn = e.target.closest('[data-pack-delete]');
    if (deleteBtn) { onDeleteClick(deleteBtn); return; }

    var editBtn = e.target.closest('[data-pack-edit]');
    if (editBtn) { onEditClick(editBtn); return; }
  });

  function actionsBox(el) {
    return el.closest('[data-pack-actions]');
  }

  /* ---- Delete: inline confirm ---- */

  function onDeleteClick(btn) {
    var box = actionsBox(btn);
    if (!box || box.getAttribute('data-confirming') === 'true') return;
    var packId = btn.getAttribute('data-pack-id') || '';
    box.setAttribute('data-confirming', 'true');
    box.dataset.restoreHtml = box.innerHTML;
    box.innerHTML =
      '<span class="cart-pack-actions__confirm-q">¿Eliminar el pack?</span>' +
      '<button type="button" class="cart-pack-action cart-pack-action--delete"' +
      ' data-pack-delete-confirm data-pack-id="' + escapeAttr(packId) + '">Sí, eliminar</button>' +
      '<button type="button" class="cart-pack-action" data-pack-delete-cancel>Cancelar</button>';
  }

  function onDeleteCancel(btn) {
    var box = actionsBox(btn);
    if (!box) return;
    if (typeof box.dataset.restoreHtml === 'string') box.innerHTML = box.dataset.restoreHtml;
    box.removeAttribute('data-confirming');
    delete box.dataset.restoreHtml;
  }

  function onDeleteConfirm(btn) {
    var packId = btn.getAttribute('data-pack-id');
    if (!packId) return;
    btn.disabled = true;
    deletePack(packId)
      .then(function () { window.location.reload(); })
      .catch(function (err) {
        console.error('Pack delete failed:', err);
        btn.disabled = false;
        showToast('No hemos podido eliminar el pack. Inténtalo otra vez.', 'error');
      });
  }

  function deletePack(packId) {
    return getCart().then(function (cart) {
      var updates = {};
      var matched = 0;
      (cart.items || []).forEach(function (item) {
        if (item.properties && item.properties[PACK_PROPERTY] === packId) {
          updates[item.key] = 0;
          matched++;
        }
      });
      if (!matched) return null;
      return fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates: updates })
      }).then(function (res) {
        if (!res.ok) throw new Error('/cart/update.js failed');
        return res.json();
      });
    });
  }

  /* ---- Edit: stash context, navigate to the pack page ---- */

  function onEditClick(btn) {
    var packId = btn.getAttribute('data-pack-id');
    var editUrl = btn.getAttribute('data-pack-edit-url') || DEFAULT_PACK_URL;
    if (!packId) return;
    btn.disabled = true;

    getCart()
      .then(function (cart) {
        var items = (cart.items || []).filter(function (item) {
          return item.properties && item.properties[PACK_PROPERTY] === packId;
        });
        if (items.length) {
          try {
            sessionStorage.setItem(EDIT_KEY, JSON.stringify(buildEditContext(packId, items)));
          } catch (e) { /* sessionStorage unavailable — configurator opens fresh */ }
        }
        window.location.href = withPreviewTheme(editUrl);
      })
      .catch(function (err) {
        console.error('Pack edit prep failed:', err);
        window.location.href = withPreviewTheme(editUrl);
      });
  }

  function buildEditContext(packId, items) {
    // Tier name from "_glend_pack_label" — format "Pack <Obrador> · <Tier>".
    var label = (items[0].properties[LABEL_PROPERTY] || '').toString();
    var tierName = '';
    var dot = label.lastIndexOf('·');
    if (dot !== -1) tierName = label.slice(dot + 1).trim();

    // slotFills keyed by slot label; a cardKey is repeated per unit quantity.
    // cardKey is the product handle for single-variant products, or
    // `<handle>::<Variant Title>` for products whose pack-slot offers per-variant cards.
    var slotFills = {};
    items.forEach(function (item) {
      var slot = (item.properties[SLOT_PROPERTY] || '').toString();
      if (!slotFills[slot]) slotFills[slot] = [];
      var qty = item.quantity || 1;
      var key = item.handle;
      if (item.variant_title && item.variant_title !== 'Default Title') {
        var vt = item.variant_title;
        if (vt.normalize) vt = vt.normalize('NFC');
        key = item.handle + '|' + vt;
      }
      for (var i = 0; i < qty; i++) slotFills[slot].push(key);
    });

    var sellingPlan = null;
    var alloc = items[0].selling_plan_allocation;
    if (alloc && alloc.selling_plan) sellingPlan = alloc.selling_plan.id;

    return {
      packInstanceId: packId,
      tierName: tierName,
      slotFills: slotFills,
      sellingPlan: sellingPlan
    };
  }

  /* ---- Helpers ---- */

  function getCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) {
      if (!r.ok) throw new Error('/cart.js failed');
      return r.json();
    });
  }

  function escapeAttr(s) {
    return String(s).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
  }

  // Carry a ?preview_theme_id through the edit navigation so unpublished-theme
  // QA keeps working. No-op in production (no preview param present).
  function withPreviewTheme(url) {
    var preview = null;
    try {
      preview = new URLSearchParams(window.location.search).get('preview_theme_id');
    } catch (_) {}
    if (!preview) return url;
    return url + (url.indexOf('?') === -1 ? '?' : '&') +
      'preview_theme_id=' + encodeURIComponent(preview);
  }

  function showToast(message, type) {
    var toast = document.createElement('div');
    toast.setAttribute('role', type === 'error' ? 'alert' : 'status');
    toast.style.cssText =
      'position:fixed;bottom:80px;left:50%;transform:translateX(-50%);' +
      'background:' + (type === 'error' ? '#B3261E' : '#2C2A2A') + ';color:#fff;' +
      'padding:12px 24px;border-radius:999px;font-size:14px;font-weight:600;' +
      'z-index:9999;box-shadow:0 4px 12px rgba(0,0,0,0.2);';
    toast.textContent = message;
    document.body.appendChild(toast);
    setTimeout(function () { toast.remove(); }, 4000);
  }
})();
